import type { ReactNode } from 'react';
import { useState } from 'react';

import type { Transition } from 'framer-motion';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import { ButtonIcon } from '@snowit/ui';
import { m } from '@snowit/ui/utils/Motion';

import Header from '../Header';
import Sidebar from '../Sidebar';

import cx from 'classnames';
import s from './Layout.module.scss';

type LayoutProps = {
  children: ReactNode;
};

const Layout = (props: LayoutProps) => {
  const { children } = props;
  const { minWidth } = useBreakpoints();

  const [shrunk, setShrunk] = useState(false);

  const handleShrunk = (bool = true) => {
    if (bool) {
      setShrunk(!shrunk);
    }
  };

  let variants = {};

  if (minWidth.sm) {
    variants = {
      open: { width: '17.5rem' },
      collapsed: { width: '1.5rem' },
    };
  } else {
    variants = {
      open: { width: '100%', opacity: 1, translateX: '-100%' },
      collapsed: { width: '100%', opacity: 1, translateX: '0%' },
    };
  }

  const transition = {
    type: 'tween',
    duration: 0.25,
    ease: [0.215, 0.61, 0.355, 1],
  } satisfies Transition;

  return (
    <div className={s.layout}>
      <m.aside
        className={cx(s.aside, { [s.shrunk]: shrunk })}
        animate={shrunk ? 'collapsed' : 'open'}
        variants={variants}
        transition={transition}
      >
        <ButtonIcon
          classNames={{ button: s.btnShrunk, wrapIcon: s.wrapIconShrunk }}
          icon={shrunk ? 'arrow_angle_right' : 'arrow_angle_left'}
          onClick={() => handleShrunk()}
          theme={'fill'}
          size={'xxl'}
          color={'var(--c-dropdown)'}
          rounded
        />

        <Sidebar shrunk={shrunk} sidebarOnClick={() => handleShrunk(!minWidth.sm)} />
      </m.aside>

      <div className={s.body}>
        <Header className={s.header} sidebarOnClick={() => handleShrunk(!minWidth.sm)} />
        <main className={s.main}>{children}</main>
      </div>
    </div>
  );
};

export default Layout;
